import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { validateEnvironment } from './config/environment';
import 'antd/dist/reset.css';
import './App.css';

// Pages
import Login from './pages/login';
import Dashboard from './components/Dashboard';
import Users from './components/Users';
import Merchants from './components/Merchants';
import Orders from './components/Orders';
import Categories from './components/Categories';
import PaymentMethods from './pages/PaymentMethods';
import Vouchers from './pages/vouchers';
import CreateVoucher from './pages/CreateVoucher';
import VoucherDetail from './pages/VoucherDetail';
import VoucherEditPage from './pages/VoucherEditPage';
import Analytics from './components/Analytics';
import Settings from './components/Settings';
import ApplicationFees from './components/ApplicationFees';
import DynamicSections from './components/DynamicSections';
import AdminManagement from './components/AdminManagement';
import AdminManagementDetail from './components/AdminManagementDetail';
import AcceptInvitationPage from './pages/AcceptInvitationPage';
import OrderDetailPage from './pages/orders/[id]';
import MerchantDetails from './pages/merchant-detail/MerchantDetails';
import AddMerchant from './pages/AddMerchant';
import MerchantEdit from './pages/MerchantEdit';
import UserManagementList from './pages/user-management/UserManagementList';
import UserDetails from './pages/user-management/UserDetails';
import SupportTicketsListPage from './pages/support-tickets/SupportTicketsListPage';
import SupportTicketDetailPage from './pages/support-tickets/SupportTicketDetailPage';
import AdsListPage from './pages/ads/AdsListPage';
import TransactionLogsListPage from './pages/transaction-logs/TransactionLogsListPage';
import MerchantRegistrationLeadsPage from './pages/merchant-registration-leads/MerchantRegistrationLeadsPage';
import MerchantKycListPage from './pages/merchant-kyc/MerchantKycListPage';
import MerchantWithdrawalRequestsListPage from './pages/withdrawal-requests/MerchantWithdrawalRequestsListPage';
import MerchantWithdrawalRequestDetailPage from './pages/withdrawal-requests/MerchantWithdrawalRequestDetailPage';
import ShopPromotionsListPage from './pages/shop-promotions/ShopPromotionsListPage';
import ShopPromotionDetailPage from './pages/shop-promotions/ShopPromotionDetailPage';
import ShopPromotionFormPage from './pages/shop-promotions/ShopPromotionFormPage';
import MerchantProgramsListPage from './pages/merchant-programs/MerchantProgramsListPage';
import MerchantProgramFormPage from './pages/merchant-programs/MerchantProgramFormPage';
import MerchantTaxPage from './pages/merchant-tax/MerchantTaxPage';
import AppotaPaySubAccountsListPage from './pages/appotapay-sub-accounts/AppotaPaySubAccountsListPage';
import AppotaPaySubAccountDetailPage from './pages/appotapay-sub-accounts/AppotaPaySubAccountDetailPage';
import { LayoutShell } from './components/layout/LayoutShell';
import { ErrorBoundary } from './components/ErrorBoundary';
import {
  DashboardOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  SettingOutlined,
  AppstoreOutlined,
  UserAddOutlined,
  TagOutlined,
  TeamOutlined,
  CustomerServiceOutlined,
  RiseOutlined,
  TransactionOutlined,
  FormOutlined,
  MoneyCollectOutlined,
  PercentageOutlined,
  CreditCardOutlined,
  GiftOutlined,
  AuditOutlined,
  BankOutlined,
} from '@ant-design/icons';

import { DEFAULT_QUERY_OPTIONS } from './config/queryConfig';
import { getRouterBasename } from './routerBasename';

const menuItems = [
  {
    key: '/',
    icon: <DashboardOutlined />,
    label: 'Tổng quan',
    path: '/',
  },
  {
    key: '/vouchers',
    icon: <TagOutlined />,
    label: 'Vouchers',
    path: '/vouchers',
  },
  {
    key: '/shop-promotions',
    icon: <PercentageOutlined />,
    label: 'KM quán (Combo & BXGY)',
    path: '/shop-promotions',
  },
  {
    key: '/merchant-programs',
    icon: <GiftOutlined />,
    label: 'Chương trình Merchant',
    path: '/merchant-programs',
  },
  {
    key: '/admin-management',
    icon: <TeamOutlined />,
    label: 'Quản lý admin',
    path: '/admin-management',
  },
  // {
  //   key: '/users',
  //   icon: <UserOutlined />,
  //   label: 'Người dùng',
  //   path: '/users',
  // },
  {
    key: '/user-management',
    icon: <UserAddOutlined />,
    label: 'Quản lý người dùng',
    path: '/user-management',
  },
  {
    key: '/merchants',
    icon: <ShopOutlined />,
    label: 'Quán',
    path: '/merchants',
  },
  {
    key: '/ads',
    icon: <RiseOutlined />,
    label: 'Quảng cáo',
    path: '/ads',
  },
  {
    key: '/orders',
    icon: <ShoppingCartOutlined />,
    label: 'Đơn hàng',
    path: '/orders',
  },
  {
    key: '/transaction-logs',
    icon: <TransactionOutlined />,
    label: 'Transaction Logs',
    path: '/transaction-logs',
  },
  {
    key: '/merchant-tax',
    icon: <AuditOutlined />,
    label: 'Thuế merchant',
    path: '/merchant-tax',
  },
  {
    key: '/withdrawal-requests',
    icon: <MoneyCollectOutlined />,
    label: 'Rút tiền merchant',
    path: '/withdrawal-requests',
  },
  {
    key: '/merchant-kyc',
    icon: <FormOutlined />,
    label: 'Duyệt KYC (app)',
    path: '/merchant-kyc',
  },
  {
    key: '/appotapay/merchants',
    icon: <BankOutlined />,
    label: 'Merchant AppotaPay (REFER)',
    path: '/appotapay/merchants',
  },
  {
    key: '/merchant-registration-leads',
    icon: <FormOutlined />,
    label: 'Đăng ký quán (web)',
    path: '/merchant-registration-leads',
  },
  {
    key: '/support-tickets',
    icon: <CustomerServiceOutlined />,
    label: 'Hỗ trợ kỹ thuật',
    path: '/support-tickets',
  },
  {
    key: '/categories',
    icon: <AppstoreOutlined />,
    label: 'Danh mục',
    path: '/categories',
  },
  {
    key: '/payment-methods',
    icon: <CreditCardOutlined />,
    label: 'Phương thức thanh toán',
    path: '/payment-methods',
  },
  // {
  //   key: '/analytics',
  //   icon: <BarChartOutlined />,
  //   label: 'Phân tích',
  //   path: '/analytics',
  // },
  // {
  //   key: '/application-fees',
  //   icon: <DollarOutlined />,
  //   label: 'Phí ứng dụng',
  //   path: '/application-fees',
  // },
  {
    key: '/settings',
    icon: <SettingOutlined />,
    label: 'Cài đặt',
    path: '/settings',
  },
  // {
  //   key: '/dynamic-sections',
  //   icon: <AppstoreOutlined />,
  //   label: 'Dynamic Sections',
  //   path: '/dynamic-sections',
  // },
];

function ProtectedRoutes() {
  const { user, logout, isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <div>Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <LayoutShell
      menuItems={menuItems}
      user={
        user
          ? { name: user.name, email: user.email, role: user.role }
          : undefined
      }
      onLogout={logout}
    >
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/vouchers" element={<Vouchers />} />
        <Route path="/vouchers/create" element={<CreateVoucher />} />
        <Route path="/vouchers/:id/edit" element={<VoucherEditPage />} />
        <Route path="/vouchers/:id" element={<VoucherDetail />} />
        <Route path="/shop-promotions" element={<ShopPromotionsListPage />} />
        <Route
          path="/shop-promotions/create"
          element={<ShopPromotionFormPage />}
        />
        <Route
          path="/shop-promotions/:id/edit"
          element={<ShopPromotionFormPage />}
        />
        <Route
          path="/shop-promotions/:id"
          element={<ShopPromotionDetailPage />}
        />
        <Route
          path="/merchant-programs"
          element={<MerchantProgramsListPage />}
        />
        <Route
          path="/merchant-programs/create"
          element={<MerchantProgramFormPage />}
        />
        <Route
          path="/merchant-programs/:id/edit"
          element={<MerchantProgramFormPage />}
        />
        <Route path="/admin-management" element={<AdminManagement />} />
        <Route
          path="/admin-management/:id"
          element={<AdminManagementDetail />}
        />
        <Route path="/users" element={<Users />} />
        <Route path="/user-management" element={<UserManagementList />} />
        <Route path="/user-management/:userId" element={<UserDetails />} />
        <Route path="/merchants" element={<Merchants />} />
        <Route path="/merchants/create" element={<AddMerchant />} />
        <Route path="/merchants/:id/edit" element={<MerchantEdit />} />
        <Route
          path="/merchants/:id"
          element={
            <ErrorBoundary>
              <MerchantDetails />
            </ErrorBoundary>
          }
        />
        <Route path="/orders" element={<Orders />} />
        <Route path="/orders/:id" element={<OrderDetailPage />} />
        <Route path="/support-tickets" element={<SupportTicketsListPage />} />
        <Route
          path="/support-tickets/:id"
          element={<SupportTicketDetailPage />}
        />
        <Route path="/ads" element={<AdsListPage />} />
        <Route path="/transaction-logs" element={<TransactionLogsListPage />} />
        <Route path="/merchant-tax" element={<MerchantTaxPage />} />
        <Route
          path="/withdrawal-requests"
          element={<MerchantWithdrawalRequestsListPage />}
        />
        <Route
          path="/withdrawal-requests/:id"
          element={<MerchantWithdrawalRequestDetailPage />}
        />
        <Route
          path="/admin/withdrawal-requests"
          element={<MerchantWithdrawalRequestsListPage />}
        />
        <Route
          path="/admin/withdrawal-requests/:id"
          element={<MerchantWithdrawalRequestDetailPage />}
        />
        <Route path="/merchant-kyc" element={<MerchantKycListPage />} />
        <Route
          path="/appotapay/merchants"
          element={<AppotaPaySubAccountsListPage />}
        />
        <Route
          path="/appotapay/merchants/:merchantId"
          element={<AppotaPaySubAccountDetailPage />}
        />
        <Route
          path="/appotapay/sub-accounts"
          element={<AppotaPaySubAccountsListPage />}
        />
        <Route
          path="/appotapay/sub-accounts/:merchantId"
          element={<AppotaPaySubAccountDetailPage />}
        />
        <Route
          path="/merchant-registration-leads"
          element={<MerchantRegistrationLeadsPage />}
        />
        <Route path="/categories" element={<Categories />} />
        <Route path="/payment-methods" element={<PaymentMethods />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/settings" element={<Settings />} />
        {/* <Route path="/application-fees" element={<ApplicationFees />} /> */}
        {/* <Route path="/dynamic-sections" element={<DynamicSections />} /> */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </LayoutShell>
  );
}

// Create a query client with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: DEFAULT_QUERY_OPTIONS,
  },
});

function App() {
  useEffect(() => {
    validateEnvironment();
  }, []);

  const basename = getRouterBasename();

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter basename={basename}>
          <Routes>
            <Route path="/login" element={<Login />} />
            {!basename ? (
              <Route
                path="/admin/login"
                element={<Navigate to="/login" replace />}
              />
            ) : null}
            <Route
              path="/invitations/accept"
              element={<AcceptInvitationPage />}
            />
            {!basename ? (
              <Route
                path="/admin/invitations/accept"
                element={<AcceptInvitationPage />}
              />
            ) : null}
            <Route path="/*" element={<ProtectedRoutes />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
